/**
 * Account Settings opens from a query param so it survives the OAuth round trip
 * of a settings Connect (Hosted UI → /auth/callback → back to the same page).
 */

import axios from 'axios';
import { getAuthMethods } from '@/api/auth';
import { readAuthSession, type AuthMethodsSummary } from '@/lib/accountLinking';

export const ACCOUNT_SETTINGS_PARAM = 'account_settings';
export const ACCOUNT_SETTINGS_OPEN_VALUE = 'open';
export const ACCOUNT_SETTINGS_ERROR_PARAM = 'account_settings_error';
export const ACCOUNT_SETTINGS_PROVIDER_PARAM = 'account_settings_provider';

export type AccountSettingsLinkError = 'ACCOUNT_LINK_REQUIRED' | 'LINK_FAILED';

export type AccountSettingsOpenSource = 'nav_more_desktop' | 'nav_more_mobile' | 'oauth_return';

const URL_BASE = 'https://account-settings.local';

const PENDING_LINK_FAILURE_KEY = 'gt_account_settings_link_failure';
const OAUTH_PENDING_KEY = 'gt_account_settings_oauth_pending';

let pendingOpenSource: AccountSettingsOpenSource | null = null;

export interface AccountSettingsOAuthPending {
  returnPath: string;
  provider: 'google' | 'apple';
}

export interface PendingAccountSettingsLinkFailure {
  code: AccountSettingsLinkError;
  provider: 'google' | 'apple';
}

function canUseSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

/** Survives OAuth return when URL params are dropped or the modal host mounts late. */
export function savePendingAccountSettingsLinkFailure(failure: PendingAccountSettingsLinkFailure): void {
  if (!canUseSessionStorage()) return;
  window.sessionStorage.setItem(PENDING_LINK_FAILURE_KEY, JSON.stringify(failure));
}

function parsePendingAccountSettingsLinkFailure(raw: string): PendingAccountSettingsLinkFailure | null {
  try {
    const parsed = JSON.parse(raw) as PendingAccountSettingsLinkFailure;
    if (!isAccountSettingsLinkError(parsed.code)) return null;
    if (parsed.provider !== 'google' && parsed.provider !== 'apple') return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Read the settings-connect failure without deleting it. */
export function peekPendingAccountSettingsLinkFailure(): PendingAccountSettingsLinkFailure | null {
  if (!canUseSessionStorage()) return null;
  const raw = window.sessionStorage.getItem(PENDING_LINK_FAILURE_KEY);
  if (!raw) return null;
  return parsePendingAccountSettingsLinkFailure(raw);
}

export function consumePendingAccountSettingsLinkFailure(): PendingAccountSettingsLinkFailure | null {
  if (!canUseSessionStorage()) return null;
  const raw = window.sessionStorage.getItem(PENDING_LINK_FAILURE_KEY);
  if (!raw) return null;
  window.sessionStorage.removeItem(PENDING_LINK_FAILURE_KEY);
  return parsePendingAccountSettingsLinkFailure(raw);
}

export function clearPendingAccountSettingsLinkFailure(): void {
  if (!canUseSessionStorage()) return;
  window.sessionStorage.removeItem(PENDING_LINK_FAILURE_KEY);
}

export function hasPendingAccountSettingsLinkFailure(): boolean {
  if (!canUseSessionStorage()) return false;
  return window.sessionStorage.getItem(PENDING_LINK_FAILURE_KEY) !== null;
}

/** Set when the user starts Connect so the modal can reopen after Hosted UI. */
export function markAccountSettingsOAuthPending(returnPath: string, provider: 'google' | 'apple'): void {
  if (!canUseSessionStorage()) return;
  const payload: AccountSettingsOAuthPending = { returnPath, provider };
  window.sessionStorage.setItem(OAUTH_PENDING_KEY, JSON.stringify(payload));
}

export function readAccountSettingsOAuthPending(): AccountSettingsOAuthPending | null {
  if (!canUseSessionStorage()) return null;
  const raw = window.sessionStorage.getItem(OAUTH_PENDING_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as AccountSettingsOAuthPending;
    if (!parsed?.returnPath || (parsed.provider !== 'google' && parsed.provider !== 'apple')) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearAccountSettingsOAuthPending(): void {
  if (!canUseSessionStorage()) return;
  window.sessionStorage.removeItem(OAUTH_PENDING_KEY);
}

export function pathHasAccountSettingsOpen(path: string): boolean {
  const url = new URL(path, URL_BASE);
  return url.searchParams.get(ACCOUNT_SETTINGS_PARAM) === ACCOUNT_SETTINGS_OPEN_VALUE;
}

export function ensureAccountSettingsOpenOnPath(path: string): string {
  const url = new URL(path, URL_BASE);
  url.searchParams.set(ACCOUNT_SETTINGS_PARAM, ACCOUNT_SETTINGS_OPEN_VALUE);
  return toRelativePath(url);
}

export function parseAppRelativePath(relativePath: string): {
  pathname: string;
  search: string;
  hash: string;
} {
  const url = new URL(relativePath, URL_BASE);
  return { pathname: url.pathname, search: url.search, hash: url.hash };
}

function pathHasAccountSettingsError(path: string): boolean {
  const url = new URL(path, URL_BASE);
  return isAccountSettingsLinkError(url.searchParams.get(ACCOUNT_SETTINGS_ERROR_PARAM));
}

/**
 * Where to send the user after a settings Connect round trip.
 * A stored link failure is stamped onto the sheet URL so a clean return path cannot drop it.
 */
export function resolveAccountSettingsOAuthReturnDestination(redirectPath: string | null): string {
  const oauthPending = readAccountSettingsOAuthPending();
  const failure = peekPendingAccountSettingsLinkFailure();
  const hinted = redirectPath || oauthPending?.returnPath || '/';
  const sheetPath = pathHasAccountSettingsOpen(hinted)
    ? hinted
    : (oauthPending?.returnPath || hinted);

  if (failure) {
    return withAccountSettingsError(sheetPath, failure.code, failure.provider);
  }
  if (pathHasAccountSettingsError(hinted)) {
    return pathHasAccountSettingsOpen(hinted) ? hinted : ensureAccountSettingsOpenOnPath(hinted);
  }
  if (pathHasAccountSettingsOpen(sheetPath)) return sheetPath;
  if (oauthPending?.returnPath) return ensureAccountSettingsOpenOnPath(oauthPending.returnPath);
  return hinted;
}

/**
 * Sheet URL for a Connect round trip that has not finished showing its result.
 * Null when this tab is not in that round trip.
 */
export function pendingAccountSettingsReturnPath(): string | null {
  if (!readAccountSettingsOAuthPending() && !peekPendingAccountSettingsLinkFailure()) return null;
  const destination = resolveAccountSettingsOAuthReturnDestination(null);
  return pathHasAccountSettingsOpen(destination) ? destination : null;
}

export function isAccountSettingsLinkError(value: unknown): value is AccountSettingsLinkError {
  return value === 'ACCOUNT_LINK_REQUIRED' || value === 'LINK_FAILED';
}

function toRelativePath(url: URL): string {
  return `${url.pathname}${url.search}${url.hash}`;
}

/** Current page with Account Settings open, used as the OAuth `redirectPath` for settings Connect. */
export function accountSettingsReturnPath(pathname: string, search: string): string {
  const url = new URL(`${pathname}${search}`, URL_BASE);
  url.searchParams.set(ACCOUNT_SETTINGS_PARAM, ACCOUNT_SETTINGS_OPEN_VALUE);
  url.searchParams.delete(ACCOUNT_SETTINGS_ERROR_PARAM);
  url.searchParams.delete(ACCOUNT_SETTINGS_PROVIDER_PARAM);
  return toRelativePath(url);
}

/** Return path for a settings Connect that failed, so the modal reopens with the error. */
export function withAccountSettingsError(
  path: string | null,
  code: AccountSettingsLinkError,
  provider: 'google' | 'apple',
): string {
  const url = new URL(path || '/', URL_BASE);
  url.searchParams.set(ACCOUNT_SETTINGS_PARAM, ACCOUNT_SETTINGS_OPEN_VALUE);
  url.searchParams.set(ACCOUNT_SETTINGS_ERROR_PARAM, code);
  url.searchParams.set(ACCOUNT_SETTINGS_PROVIDER_PARAM, provider);
  return toRelativePath(url);
}

/** Removes every Account Settings param (closing the modal). */
export function withoutAccountSettingsParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(ACCOUNT_SETTINGS_PARAM);
  next.delete(ACCOUNT_SETTINGS_ERROR_PARAM);
  next.delete(ACCOUNT_SETTINGS_PROVIDER_PARAM);
  return next;
}

export function setPendingAccountSettingsSource(source: AccountSettingsOpenSource): void {
  pendingOpenSource = source;
}

/** Source set by the menu that opened the modal; anything else arrived via redirect. */
export function consumePendingAccountSettingsSource(): AccountSettingsOpenSource {
  const source = pendingOpenSource ?? 'oauth_return';
  pendingOpenSource = null;
  return source;
}

const TOKEN_WAIT_MS = 2500;
const TOKEN_POLL_MS = 50;
const METHODS_RETRY_DELAY_MS = 400;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function waitForAccessToken(maxMs = TOKEN_WAIT_MS): Promise<void> {
  const deadline = Date.now() + maxMs;
  while (Date.now() < deadline) {
    if (readAuthSession().accessToken) return;
    await sleep(TOKEN_POLL_MS);
  }
}

/** True for missing token, network blips, and auth/upstream errors worth one silent retry. */
export function isRetriableAuthMethodsLoadError(err: unknown): boolean {
  if (err instanceof Error && err.message.includes('Authentication token not found')) {
    return true;
  }
  if (!axios.isAxiosError(err)) {
    return err instanceof TypeError;
  }
  if (!err.response) return true;
  const status = err.response.status;
  return status === 401 || status === 403 || status === 408 || status === 429 || status >= 500;
}

/** Loads sign-in methods after auth tokens are ready, with one retry for transient failures. */
export async function loadAuthMethodsForAccountSettings(): Promise<AuthMethodsSummary> {
  await waitForAccessToken();
  try {
    return await getAuthMethods();
  } catch (first) {
    if (!isRetriableAuthMethodsLoadError(first)) throw first;
    await sleep(METHODS_RETRY_DELAY_MS);
    return await getAuthMethods();
  }
}
